// @flow
import React, { useState, useEffect } from 'react'
import Avatar from '@components/app/avatar'
import Button from '@components/common/button'
import Spinner from '@components/common/spinner'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { getMe } from '@/data/api/auth'
import useAuth from '@/hooks/auth'
import SearchInput from '../views/search/input'
import SearchMedia from '../views/search/media'
import SearchTag from '../views/search/tag'
import { searchUser, searchSound } from '@/utils/algolia/search'
import { getToken } from '@/utils/token'

const PER_PAGE = 20

interface Props {
  q: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  medias: Array<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  users: Array<any>
}

const Search = ({ q, medias, users }: Props) => {
  const { currentUser } = useAuth()
  const [ query, setQuery ] = useState(q)
  const [ page, setPage ] = useState(1)
  const [ mediaList, setMediaList ] = useState(medias)
  const [ usersList, setUsersList ] = useState(users)
  const [ isMore, setMore ] = useState(true)
  const [ isLoading, setLoading ] = useState(false)
  const router = useRouter()
  const [ tags, setTags ] = useState([])

  const onSearch = async (searchQuery: string) => {
    setQuery(searchQuery)
    router.push(`/search?q=${searchQuery}`, undefined, { shallow: true })
    const usersData = await searchUser({ query: searchQuery, userId: currentUser?.id })
    const mediasData = await searchSound({
      query: searchQuery,
      hitsPerPage: PER_PAGE,
      userId: currentUser?.id,
    })
    setUsersList(usersData)
    setMediaList(mediasData)
  }

  const getTag = async (mediaData) => {
    if (mediaData.length <= 0) {
      return
    }
    let tagList = []
    mediaData.map((media) => {
      if (media.tags) {
        tagList = [ ...tagList, ...media.tags ]
      }
      return tagList
    })

    if (tagList.length <= 0) {
      return
    }
    setTags([ ...tags, ...tagList.filter((x, i, self) => self.indexOf(x) === i) ])
  }

  const loadMore = async () => {
    setLoading(true)
    const result = await searchSound({
      query,
      page: page + 1,
      hitsPerPage: PER_PAGE,
      userId: currentUser?.id,
    })
    setMediaList([ ...mediaList, ...result ])
    setPage(page + 1)
    getTag(result)
    setMore(result.length >= PER_PAGE)
    setLoading(false)
  }

  useEffect(() => {
    getTag(mediaList)
    setMore(mediaList.length >= PER_PAGE)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ mediaList ])

  return (
    <div className="container-md">
      <SearchInput q={q} onSearch={onSearch} />
      <div className="row flex-nowrap">
        <div className="col-md-8">
          <h2 className="tx-oswald tx-16 tx-gish tx-w300 mb-4">
              Sound{/* / Albums */}
          </h2>
          <SearchMedia medias={mediaList} />
          {isMore && (
            <div className="tx-center mb-5">
              {isLoading ? (
                <>
                  <Spinner size="1.25rem" />
                  <span className="tx-oswald tx-w300 ml-2">Loading</span>
                </>
              ) : (
                <Button className="lg" onClick={loadMore}>
                    Load more
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="col-md-4 hidden-sm">
          <SearchTag tags={tags} />
          {usersList.length > 0 && (
            <div>
              <h2 className="tx-oswald tx-16 tx-gish tx-w300 mb-4">Users</h2>
              {usersList.map((user) => (
                <div key={user.username} className="d-flex mb-5">
                  <div className="flex0 mr-3">
                    <Avatar
                      src={user.user_image}
                      width="80"
                    />
                  </div>
                  <div className="flex1">
                    <h3 className="tx-18 tx-w700 mb-0">
                      <Link
                        href={`/user/${user.username}`}
                        className="alt"
                      >
                        {user.name}
                      </Link>
                    </h3>
                    <div className="tx-14 tx-g600 mb-2">@{user.username}</div>
                    {user.shortBio && (
                      <p className="tx-14 tx-gish tx-ellipsis3 mb-0">
                        {user.shortBio}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

Search.getInitialProps = async ({ req, query }) => {
  const { q } = query

  let userId: string
  const token = await getToken(req)
  if (token) {
    const user = await getMe(token)
      .catch((error) => ({ error }))
    if (!user.error) {
      userId = user.id
    }
  }

  const users = await searchUser({ query: q, userId })
  const medias = await searchSound({ query, hitsPerPage: PER_PAGE, userId })

  return {
    q,
    users,
    medias,
    breadcrumbs: [
      {
        label: 'Search',
        to: '/search',
      },
      {
        label: q,
      },
    ],
  }
}

export default Search
